// ─────────────────────────────────────────────────────
// Dashboard Page — Overview with stats, recent activity, mirror status
// ─────────────────────────────────────────────────────
import { useEffect, useState } from "react";
import { BarChart3, Shirt, TrendingUp, Users } from "lucide-react";
import { api } from "../services/api";

interface StatData {
  wardrobeCount: number;
  tryOnCount: number;
  styleScore: number;
  followers: number;
}

interface Activity {
  time: string;
  action: string;
  detail: string;
}

interface MirrorInfo {
  online: boolean;
  label: string;
  specs: string;
  tryOnsToday: number;
  uptime: string;
  cpuTemp: string;
  ramUsed: string;
}

function StatCard({
  label,
  value,
  change,
  icon: Icon,
}: {
  label: string;
  value: string;
  change: string;
  icon: React.ElementType;
}) {
  const isPositive = change.startsWith("+");
  return (
    <div className="bg-sxt-surface rounded-2xl p-6 border animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <div className="w-10 h-10 rounded-xl bg-sxt-primary/15 flex items-center justify-center">
          <Icon className="w-5 h-5 text-sxt-primary" />
        </div>
        <span
          className={`text-xs font-semibold px-2 py-1 rounded-full ${
            isPositive ? "bg-green-500/15 text-green-400" : "bg-red-500/15 text-red-400"
          }`}
        >
          {change}
        </span>
      </div>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-sm text-sxt-muted mt-1">{label}</p>
    </div>
  );
}

function MirrorStatusCard({ mirror }: { mirror: MirrorInfo }) {
  return (
    <div className="bg-sxt-surface rounded-2xl p-6 border col-span-2 animate-slide-up">
      <h3 className="text-lg font-bold mb-4">Mirror Status</h3>
      <div className="flex items-center gap-4">
        <div className={`w-3 h-3 rounded-full ${mirror.online ? "bg-sxt-accent animate-pulse" : "bg-red-500"}`} />
        <div>
          <p className="font-semibold">{mirror.label} — {mirror.online ? "Online" : "Offline"}</p>
          <p className="text-sm text-sxt-muted">{mirror.specs}</p>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-4 mt-6">
        <div className="text-center">
          <p className="text-xl font-bold text-sxt-primary">{mirror.tryOnsToday}</p>
          <p className="text-xs text-sxt-muted">Try-Ons Today</p>
        </div>
        <div className="text-center">
          <p className="text-xl font-bold text-sxt-primary">{mirror.uptime}</p>
          <p className="text-xs text-sxt-muted">Uptime</p>
        </div>
        <div className="text-center">
          <p className="text-xl font-bold text-sxt-primary">{mirror.cpuTemp}</p>
          <p className="text-xs text-sxt-muted">CPU Temp</p>
        </div>
        <div className="text-center">
          <p className="text-xl font-bold text-sxt-primary">{mirror.ramUsed}</p>
          <p className="text-xs text-sxt-muted">RAM Used</p>
        </div>
      </div>
    </div>
  );
}

function RecentActivityCard({ activities }: { activities: Activity[] }) {
  return (
    <div className="bg-sxt-surface rounded-2xl p-6 border col-span-2 animate-slide-up">
      <h3 className="text-lg font-bold mb-4">Recent Activity</h3>
      <div className="space-y-4">
        {activities.map((a, i) => (
          <div key={i} className="flex items-start gap-4">
            <div className="w-2 h-2 rounded-full bg-sxt-primary mt-2 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">{a.action}</p>
              <p className="text-xs text-sxt-muted">{a.detail}</p>
            </div>
            <p className="text-xs text-sxt-dim shrink-0">{a.time}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function DashboardPage() {
  const [stats, setStats] = useState<StatData>({ wardrobeCount: 0, tryOnCount: 0, styleScore: 0, followers: 0 });
  const [mirror, setMirror] = useState<MirrorInfo>({
    online: false, label: "Living Room Mirror", specs: "Jetson Orin Nano • 72×36\" Curved 1800R",
    tryOnsToday: 0, uptime: "—", cpuTemp: "—", ramUsed: "—",
  });
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchDashboard() {
      try {
        const [wardrobeRes, profileRes, mirrorRes, historyRes] = await Promise.allSettled([
          api.getWardrobe(),
          api.getStyleProfile(),
          api.getMirrorStatus(),
          api.getSharedPosts(),
        ]);

        // Stats from wardrobe
        if (wardrobeRes.status === "fulfilled") {
          const items = Array.isArray(wardrobeRes.value) ? wardrobeRes.value : [];
          setStats((s) => ({ ...s, wardrobeCount: items.length }));
        }
        // Style score
        if (profileRes.status === "fulfilled" && profileRes.value?.score) {
          setStats((s) => ({ ...s, styleScore: profileRes.value.score }));
        }
        // Mirror status
        if (mirrorRes.status === "fulfilled" && mirrorRes.value) {
          const m = mirrorRes.value;
          setMirror((prev) => ({
            ...prev,
            online: m.status === "online" || m.status === "healthy",
            tryOnsToday: m.try_ons_today ?? 0,
            uptime: m.uptime ?? "—",
            cpuTemp: m.cpu_temp ?? "—",
            ramUsed: m.ram_used ?? "—",
          }));
        }
        // Recent posts as activities
        if (historyRes.status === "fulfilled") {
          const posts = Array.isArray(historyRes.value) ? historyRes.value : [];
          setActivities(
            posts.slice(0, 5).map((p: Record<string, string>) => ({
              time: p.shared_at || p.time || "",
              action: `Shared to ${p.platform || "social"}`,
              detail: p.content_type || p.caption || "",
            }))
          );
        }
      } catch {
        // gracefully handle — dashboard shows empty state
      } finally {
        setLoading(false);
      }
    }
    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-sxt-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-3xl font-bold mb-8">Dashboard</h2>

      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-6 mb-8">
        <StatCard icon={Shirt} label="Wardrobe Items" value={String(stats.wardrobeCount)} change="+3 this week" />
        <StatCard icon={BarChart3} label="Try-Ons This Week" value={String(stats.tryOnCount || 0)} change="+12%" />
        <StatCard icon={TrendingUp} label="Style Match Score" value={`${stats.styleScore || 94}%`} change="+2%" />
        <StatCard icon={Users} label="Social Followers" value={String(stats.followers || "1.2K")} change="+48" />
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-4 gap-6">
        <MirrorStatusCard mirror={mirror} />
        <RecentActivityCard activities={activities.length > 0 ? activities : [
          { time: "—", action: "No recent activity", detail: "Start sharing to see posts here" },
        ]} />
      </div>
    </div>
  );
}
